<script setup lang="ts" generic="ModelValue">
import { RadioGroupItem, RadioGroupRoot } from 'radix-vue'
import type { InputOption } from '../types'
import YscInputGroup from './YscInputGroup.vue'
import { defineModel, computed } from '#imports'

interface Props {
  name: string
  options: InputOption<ModelValue>[]
  required?: boolean
  disabled?: boolean
  grow?: boolean
}

const props = withDefaults(defineProps<Props>(), {
  options: () => [],
  required: false,
  disabled: false,
  grow: false,
})

const modelValue = defineModel<ModelValue>()

const proxyModel = computed({
  get() {
    return modelValue.value === undefined ? undefined : JSON.stringify(modelValue.value)
  },
  set(value) {
    if (value === undefined) {
      modelValue.value = undefined
    }
    else {
      modelValue.value = JSON.parse(value)
    }
  },
})
</script>

<template>
  <YscInputGroup
    :required="props.required"
    :disabled="props.disabled"
  >
    <template
      v-if="$slots.default"
      #default
    >
      <slot />
    </template>

    <template #inputBlock>
      <div>
        <RadioGroupRoot
          v-model="proxyModel"
          :name="name"
          :required="required"
          :disabled="disabled"
          loop
          orientation="horizontal"
          :class="[props.grow ? 'flex' : 'inline-flex']"
          class="justify-between rounded-sm border yscToogleGroupRoot"
        >
          <RadioGroupItem
            v-for="option in props.options"
            :key="`toggle_group_${name}_${option.value}`"
            class="yscToogleGroupItem"
            :value="JSON.stringify(option.value)"
            :disabled="option.disabled"
          >
            {{ option.text }}
          </RadioGroupItem>
        </RadioGroupRoot>
      </div>
    </template>
  </YscInputGroup>
</template>

<style scoped>
.yscToogleGroupItem{@apply w-full cursor-pointer whitespace-pre px-4 py-3 text-center border-s border-gray-300 outline-none;@apply transition-all ease-linear duration-100 motion-reduce:transition-none}.yscToogleGroupItem:hover{@apply text-gray-700 bg-white bg-gray-100}.yscToogleGroupItem:first-child{@apply rounded-s-sm border-none}.yscToogleGroupItem:last-child{@apply rounded-e-sm}.yscToogleGroupItem[data-state=checked]{@apply ring-[3px] ring-black rounded-sm z-[1] bg-white text-black}.yscToogleGroupItem[data-disabled]{@apply bg-gray-50 text-gray-500 cursor-not-allowed}.yscToogleGroupRoot:has(.yscToogleGroupItem:focus-visible){@apply ring-[3px] ring-offset-4 ring-peacock-200}
</style>
